import { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { MenuItem, TextField, Chip, Grid, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import agent from "../api/agent";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { GameDto } from "../models/gameDto";
import { FormState } from "../models/formState";
import * as XLSX from "xlsx";
import { formatSecondsToMMSS } from "../utils/formatSeconds";

interface CampaignData {
  id: string;
  name: string;
  formState: FormState;
  gameType: number;
}

export default function ListPlayers() {
  const [games, setGames] = useState<GameDto[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
  const [type, setType] = useState<number | undefined>(undefined);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(100); // MUI MIT versiyonunda max 100
  const [rowCount, setRowCount] = useState<number>(0); // Dinamik olarak güncellenecek

  const fetchGames = async (campaignId?: string, pageId?: number, pageSize?: number, type?: number) => {
    setLoading(true);
    try {
      // API'de pageId 0 tabanlı, DataGrid 0 tabanlı
      const response = await agent.Games.list(campaignId, pageId, pageSize, type);
      const formattedGames = response.map((game) => ({
        ...game,
        timeCompletedFormatted:
          game.timeCompleted !== null && game.timeCompleted !== 0 && game.timeCompleted !== undefined
            ? formatSecondsToMMSS(game.timeCompleted)
            : ""
      }));
      setGames(formattedGames);
      
      // Backend'den gelen veri sayısına göre rowCount'u güncelle
      const currentPageId = pageId ?? 0;
      const currentPageSize = pageSize ?? 100;
      
      if (response.length < currentPageSize) {
        // Eğer backend'den pageSize'dan az veri geldiyse, bu son sayfa demektir
        setRowCount(currentPageId * currentPageSize + response.length);
      } else {
        // Eğer tam pageSize kadar veri geldiyse, daha fazla veri olabilir
        setRowCount((currentPageId + 1) * currentPageSize + 1); // Bir sonraki sayfada veri olabilir
      }
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Oyuncular getirilemedi.");
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await agent.Campaigns.listLight();
      setCampaigns(
        response.map((campaign) => ({ 
          id: campaign.id, 
          name: campaign.title, 
          formState: campaign.formState,
          gameType: campaign.gameType || 1
        }))
      );
    } catch (error) {
      console.log(error);
      toast.error("Kampanyalar getirilemedi.");
    }
  };

  // DataGrid'in pagination modeli değiştiğinde çağrılır
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  useEffect(() => {
    fetchGames(selectedCampaignId, page, pageSize, type);
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, page, pageSize, type]);

  const handleCampaignChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCampaignId(event.target.value);
    setType(undefined);
    setPage(0); // Kampanya değişince sayfa başa dönsün
  };

  const handleGameTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    setType(selectedValue === "" ? undefined : parseInt(selectedValue));
    setSelectedCampaignId(""); // Reset campaign
    setPage(0);
  };

  const gamesWithGameType = games.map(game => ({
    ...game,
    gameType: campaigns.find(c => c.id === game.campaignId)?.gameType || 1
  }));

  const filteredGames = gamesWithGameType.filter((game) => {
    const campaignMatch = selectedCampaignId ? game.campaignId === selectedCampaignId : true;
    const gameTypeMatch = type !== undefined ? game.gameType === type : true;
    return campaignMatch && gameTypeMatch;
  });

  const columns: GridColDef[] = [
    {
      field: "datePlayed",
      headerName: "Oynanma Tarihi",
      width: 160,
      renderCell: (params: any) =>
        dayjs(params.row.datePlayed).add(3, 'hour').format("DD.MM.YYYY HH:mm"),
    },
    {
      field: "prize",
      headerName: "Ödül",
      width: 160,
      renderCell: (params: any) =>
        params.row.isPrize ? (
          params.row.prize
        ) : (
          <Chip label="Kazanamadı" color="error" />
        ),
    },
    {
      field: "timeCompletedFormatted",
      headerName: "Tamamladığı Süre",
      width: 140,
    },
    {
      field: "name",
      headerName: "Ad Soyad",
      width: 140,
    },
    {
      field: "gender",
      headerName: "Cinsiyet",
      width: 80,
      renderCell: (params: any) =>
        params.row.name !== " " && params.value !== "Male" ? (
          <Chip
            label={
              params.value === "1"
                ? "Erkek"
                : params.value === "2"
                ? "Kadın"
                : "Erkek"
            }
          />
        ) : "",
    },
    {
      field: "email",
      headerName: "E-posta",
      width: 140,
    },
    {
      field: "phone",
      headerName: "Telefon",
      width: 140,
    },
    {
      field: "company",
      headerName: "Firma",
      width: 140,
    },
    {
      field: "bonusField",
      headerName: "Ek Alan",
      width: 160,
    },
    {
      field: "campaignName",
      headerName: "Kampanya Adı",
      width: 180,
    },
    {
      field: "gameType",
      headerName: "Oyun Tipi",
      width: 120,
      renderCell: (params: any) => {
        const gameType = params.value;
        let gameTypeText = "";
        let chipColor: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
        
        switch (gameType) {
          case 1:
            gameTypeText = "Çarkıfelek";
            chipColor = "primary";
            break;
          case 2:
            gameTypeText = "Puzzle";
            chipColor = "secondary";
            break;
          case 3:
            gameTypeText = "Kazı Kazan";
            chipColor = "warning";
            break;
          case 4:
            gameTypeText = "Dokun Kazan";
            chipColor = "error";
            break;
          case 5:
            gameTypeText = "Eşle Kazan";
            chipColor = "success";
            break;
          case 6:
            gameTypeText = "Quiz";
            chipColor = "error";
            break;
            case 7:
            gameTypeText = "Düşenleri Yakala";
            chipColor = "secondary";
            break;
          default:
            gameTypeText = "Bilinmiyor";
            chipColor = "default";
        }
        
        return <Chip label={gameTypeText} color={chipColor} size="small" />;
      },
    },
  ];

  const handleOnExport = async () => {
    const selectedCampaign = campaigns.find(
      (campaign) => campaign.id === selectedCampaignId
    );
    let exportRows = filteredGames;
    if (selectedCampaignId) {
      exportRows = await agent.Games.list(selectedCampaignId);
    }

    const fs = selectedCampaign?.formState;
    const includePerm1 = !!(fs && fs.formAddPermission && fs.formAddPermission.trim() !== "");
    const includePerm2 = !!(fs && fs.formAddPermission2 && fs.formAddPermission2.trim() !== "");
    const perm1Title = fs?.formAddPermissionTitle || "İzin 1";
    const perm2Title = fs?.formAddPermission2Title || "İzin 2";
    let bonusFieldLabel = fs?.bonusField.split(":")[0] || "Ek Alan"

    const filteredData = exportRows.map((game) => {
      const playerData: any = {
        gameType: game.gameType === 1 ? "Çarkıfelek" : game.gameType === 2 ? "Puzzle" : game.gameType === 3 ? "Kazı Kazan" : game.gameType === 4 ? "Dokun Kazan" : game.gameType === 5 ? "Eşle Kazan" : game.gameType === 6 ? "Quiz" : game.gameType === 7 ? "Düşenleri Yakala" : "",
        datePlayed: dayjs(game.datePlayed).add(3, 'hour').format("DD.MM.YYYY HH:mm"),
        prize: game.isPrize ? game.prize : "Kazanamadı",
        name: game.name,
      };
      if ([2, 4].includes(game.gameType) && typeof game.timeCompleted === 'number') {
        playerData.timeCompleted = game.timeCompletedFormatted;
      }
      if (selectedCampaignId) {
        if (fs?.formEmail) playerData.email = game.email;
        if (fs?.formPhone) playerData.phone = game.phone;
        if (fs?.formCompany) playerData.company = game.company;
        if (fs?.formGender) playerData.gender = game.name !== " " ? game.gender === "2" ? "Kadın" : "Erkek" : "";
        if (fs?.bonusField) playerData.bonusField = game.bonusField.split(":").pop();

        if (includePerm1) {
          playerData[perm1Title] =
            game.formAddPermissionChecked === true
              ? "Evet"
              : game.formAddPermissionChecked === false
              ? "Hayır"
              : "";
        }
        if (includePerm2) {
          playerData[perm2Title] =
            game.formAddPermission2Checked === true
              ? "Evet"
              : game.formAddPermission2Checked === false
              ? "Hayır"
              : "";
        }
      } else {
        playerData.email = game.email;
        playerData.phone = game.phone;
        playerData.company = game.company;
        playerData.gender = game.name != " " && game.gender !== "Male" ? game.gender === "2" ? "Kadın" : "Erkek" : "";
        playerData.bonusField = game.bonusField;
        bonusFieldLabel = "Ek Alan";
      }
      return playerData;
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const campaignName = selectedCampaign ? selectedCampaign.name.toLowerCase().replace(/\s+/g, '-') : "tüm-kampanyalar";
    const currentDate = dayjs().format("DD-MM-YYYY");

    const headers = ["Oyun Tipi", "Oynanma Tarihi", "Ödül"];
    if (filteredGames.some(g => [2, 4].includes(g.gameType))) headers.push("Tamamladığı Süre");
    headers.push("Ad Soyad")
    if (selectedCampaignId) {
      if (fs?.formEmail) headers.push("E-Posta");
      if (fs?.formPhone) headers.push("Telefon");
      if (fs?.formCompany) headers.push("Firma");
      if (fs?.formGender) headers.push("Cinsiyet");
      if (fs?.bonusField) headers.push(bonusFieldLabel);
      if (includePerm1) headers.push(perm1Title);
      if (includePerm2) headers.push(perm2Title);
    } else {
      headers.push("E-Posta");
      headers.push("Telefon");
      headers.push("Firma");
      headers.push("Cinsiyet");
      headers.push(bonusFieldLabel);
    }

    headers.forEach((header, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      worksheet[cellRef] = { t: "s", v: header, s: { font: { bold: true } } };
    });

    const headerStyle = { font: { bold: true } };
    const rangeRef = worksheet["!ref"];
    if (rangeRef) {
      // Ensure that !ref is not undefined
      const range = XLSX.utils.decode_range(rangeRef);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ c: C, r: 0 });
        if (!worksheet[address]) continue;
        worksheet[address].s = headerStyle;
      }
    }

    const columnWidths = headers.map(() => ({ wch: 22 }));;
    worksheet["!cols"] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "DijitalMiniOyun");

    XLSX.writeFile(
      workbook,
      `${campaignName}-${currentDate}-oyuncu-listesi.xlsx`
    );
  };

  // Type filtresi için seçenekler
  const typeOptions = [
    { value: undefined, label: "Tümü" },
    { value: 1, label: "Çarkıfelek" },
    { value: 2, label: "Puzzle" },
    { value: 3, label: "Kazı Kazan" },
    { value: 4, label: "Dokun Kazan" },
    { value: 5, label: "Eşle Kazan" },
    { value: 6, label: "Quiz" },
    { value: 7, label: "Düşenleri Yakala" },
  ];

  return (
    <>
      <AdminLayout title="Oyuncular">
        <Grid container spacing={2} marginBottom={2} sx={{ justifyContent: "space-between" }}>
          <Grid item xs={6} sm={6} md={4}>
            <TextField
              select
              label="Kampanya Seç"
              value={selectedCampaignId}
              onChange={handleCampaignChange}
              fullWidth
              variant="outlined"
            >
              <MenuItem value="">
                <em>Tümü</em>
              </MenuItem>
              {campaigns.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <TextField
              select
              label="Oyun Tipi"
              value={type ?? ""}
              onChange={handleGameTypeChange}
              fullWidth
              variant="outlined"
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value ?? "all"} value={option.value ?? ""}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              color="success"
              onClick={handleOnExport}
            >
              Excel Çıktı Al
            </Button>
          </Grid>
        </Grid>
        <DataGrid
          rows={filteredGames}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[50, 100]}
          checkboxSelection
        />
      </AdminLayout>
    </>
  );
}
